import { Button } from "components/ui/Button";
import { UNNAMED_DESTINATION } from "features/pregnancy/utils/receivingHospital";

import style from "./SendMncmsButton.module.css";

const Spinner = () => <span className={style.spinner} aria-hidden="true" />;

export const SendMncmsButton = ({
  consultationId,
  isSendReady,
  isSent,
  isSendPending,
  sendError,
  missingFields,
  onSend,
  onRetry,
  isAwaitingInbound = false,
  /*
   * The action, without the destination — "Send", "Save, and send", "Finish, and send". The
   * hospital's name is appended here rather than baked into each caller's string, so every send
   * button names the facility the message will actually carry in MSH.6 (KIN-2031).
   */
  sendActionLabel = "Send",
  destinationName,
}) => {
  const label = `${sendActionLabel} to ${destinationName || UNNAMED_DESTINATION}`;

  // Shared care is hospital-initiated: the GP may not send (first send OR
  // correction) until the maternity hospital has sent at least one inbound
  // message for this pregnancy (KIN-1086 / BE gate KIN-1054). Checked first so
  // it can't be bypassed by the ready/sent/retry branches below.
  if (isAwaitingInbound) {
    const disabledLabel = isSent ? "Send correction" : label;

    return (
      <div className={style.notReadyWrapper}>
        <p className={style.missingFields}>
          Awaiting the first message from the maternity hospital — shared care is
          initiated by the hospital, so they send first.
        </p>
        <Button
          variant="outline"
          type="button"
          disabled
          title="Awaiting the first message from the maternity hospital"
        >
          {disabledLabel}
        </Button>
      </div>
    );
  }

  // Required fields gate every send — a first send AND a correction. Shown as a
  // disabled button with the list of what's still missing, before the
  // already-sent / retry branches so it can't be bypassed.
  if (!isSendReady) {
    const disabledLabel = isSent ? "Send correction" : label;

    return (
      <div className={style.notReadyWrapper}>
        {!!missingFields?.length && (
          <p className={style.missingFields}>
            Required: {missingFields.join(", ")}
          </p>
        )}
        <Button
          variant="outline"
          type="button"
          disabled
          title={`Required before sending: ${missingFields.join(", ")}`}
        >
          {disabledLabel}
        </Button>
      </div>
    );
  }

  // Already sent → a correction. Save the current edits first, then send
  // (onSend === save + send; the send routes to the correction endpoint
  // because the visit is already sent), so in-form changes are included.
  if (isSent) {
    return (
      <Button
        variant="outline"
        type="button"
        onClick={() => onSend(consultationId)}
        disabled={isSendPending}
      >
        {isSendPending ? <><Spinner /> Sending…</> : "Send correction"}
      </Button>
    );
  }

  // Initial send failed (nothing sent yet) → just re-send the already-saved
  // consultation, without re-saving (avoids overwriting with a null date).
  if (sendError) {
    const handleRetry = onRetry ?? (() => onSend(consultationId));

    return (
      <Button
        variant="outline"
        type="button"
        onClick={handleRetry}
        disabled={isSendPending}
      >
        {isSendPending ? <><Spinner /> Sending…</> : "Retry Send"}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      type="button"
      onClick={() => onSend(consultationId)}
      disabled={isSendPending}
    >
      {isSendPending ? <><Spinner /> Sending…</> : label}
    </Button>
  );
};
